import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import { getColumns } from "./getColumns";
import TaskSchedulerService from "./TaskSchedulerService";
import { TASK_SCHEDULER_TYPES } from "./taskSchedulerConstants";
import { ADMIN_TASK_SCHEDULER_NEW_PATH, adminTaskSchedulerEditPath } from "./taskSchedulerRoutes";

const TYPE_FILTER_OPTIONS = [{ value: "all", label: "All" }, ...TASK_SCHEDULER_TYPES];
const TYPE_FILTER_KEY = "admin.taskScheduler.typeFilter";
const ENABLED_FILTER_OPTIONS = [
    { value: "enabled", label: "Enabled only" },
    { value: "all", label: "All" },
];
const ENABLED_FILTER_KEY = "admin.taskScheduler.enabledFilter";

function readStoredFilter(key, options, fallback) {
    try {
        const stored = localStorage.getItem(key);
        if (!stored) return fallback;
        const known = options.some((opt) => opt.value === stored);
        return known ? stored : fallback;
    } catch {
        return fallback;
    }
}

function writeStoredFilter(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {
        /* ignore quota / private mode */
    }
}

export default function TaskSchedulerMain() {
    const navigate = useNavigate();
    const location = useLocation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reload, setReload] = useState();
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [totalRows, setTotalRows] = useState(0);
    const [sortModel, setSortModel] = useState([{ field: "createdAt", sort: "desc" }]);
    const [typeFilter, setTypeFilter] = useState(() => readStoredFilter(TYPE_FILTER_KEY, TYPE_FILTER_OPTIONS, "all"));
    const [enabledFilter, setEnabledFilter] = useState(() => readStoredFilter(ENABLED_FILTER_KEY, ENABLED_FILTER_OPTIONS, "all"));
    const [duplicatingId, setDuplicatingId] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [rowToDelete, setRowToDelete] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState("");

    const fetchList = async () => {
        setLoading(true);
        const sort =
            sortModel.length > 0 && sortModel[0].field && sortModel[0].sort
                ? `${sortModel[0].field}:${sortModel[0].sort}`
                : "createdAt:desc";
        const result = await TaskSchedulerService.list(
            paginationModel.page,
            paginationModel.pageSize,
            sort,
            typeFilter,
            enabledFilter
        );
        setLoading(false);
        if (result.success && result.data) {
            setRows(result.data.content || []);
            setTotalRows(result.data.totalElements ?? result.data.page?.totalElements ?? 0);
        } else {
            setSeverity("error");
            setMessage(result.error || "Failed to fetch task schedules");
            setOpenSnackbar(true);
        }
    };

    useEffect(() => {
        fetchList();
    }, [reload, paginationModel, sortModel, typeFilter, enabledFilter]);

    useEffect(() => {
        const msg = location.state?.message;
        if (msg) {
            setSeverity("success");
            setMessage(msg);
            setOpenSnackbar(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.message, location.pathname, navigate]);

    const handleToggleEnabled = async (row, checked) => {
        const result = await TaskSchedulerService.update(row.id, { enabled: checked });
        if (result.success) {
            if (enabledFilter === "enabled" && !checked) {
                setReload(moment());
            } else {
                setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, enabled: checked } : r)));
            }
        } else {
            setSeverity("error");
            setMessage(result.error || "Failed to update enabled");
            setOpenSnackbar(true);
            setReload(moment());
        }
    };

    const handleDuplicate = async (row) => {
        if (!row?.id || duplicatingId) return;
        setDuplicatingId(row.id);
        const loaded = await TaskSchedulerService.getById(row.id);
        const source = loaded.success && loaded.data ? loaded.data : row;
        const memoText = typeof source.memo === "string" ? source.memo.trim() : "";
        const payload =
            source.payload && typeof source.payload === "object" && !Array.isArray(source.payload)
                ? { ...source.payload }
                : {};
        const result = await TaskSchedulerService.create({
            cron: source.cron,
            type: source.type,
            enabled: false,
            memo: memoText ? `${memoText} (copy)` : "Copy",
            payload,
        });
        setDuplicatingId(null);
        if (result.success) {
            setSeverity("success");
            setMessage("Schedule copied (disabled until you enable it)");
            setOpenSnackbar(true);
            setReload(moment());
        } else {
            setSeverity("error");
            setMessage(result.error || "Failed to copy schedule");
            setOpenSnackbar(true);
        }
    };

    const handleRemoveClick = (row) => {
        if (!row?.id || duplicatingId || removingId) return;
        setRowToDelete(row);
        setOpenConfirmDialog(true);
    };

    const handleConfirmDelete = async (id) => {
        if (!id) return;
        setRemovingId(id);
        const result = await TaskSchedulerService.delete(id);
        setRemovingId(null);
        setRowToDelete(null);
        if (result.success) {
            setSeverity("success");
            setMessage("Schedule removed");
            setOpenSnackbar(true);
            setReload(moment());
        } else {
            setSeverity("error");
            setMessage(result.error || "Failed to remove schedule");
            setOpenSnackbar(true);
        }
    };

    const columns = getColumns({
        onToggleEnabled: handleToggleEnabled,
        onDuplicate: handleDuplicate,
        onRemove: handleRemoveClick,
        duplicatingId,
        removingId,
    });

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="h5" component="h1">
                    Task Scheduler
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(ADMIN_TASK_SCHEDULER_NEW_PATH)}
                >
                    Add schedule
                </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="task-scheduler-enabled-filter-label">Enabled</InputLabel>
                    <Select
                        labelId="task-scheduler-enabled-filter-label"
                        id="task-scheduler-enabled-filter"
                        value={enabledFilter}
                        label="Enabled"
                        onChange={(e) => {
                            setEnabledFilter(e.target.value);
                            writeStoredFilter(ENABLED_FILTER_KEY, e.target.value);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                    >
                        {ENABLED_FILTER_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel id="task-scheduler-type-filter-label">Type</InputLabel>
                    <Select
                        labelId="task-scheduler-type-filter-label"
                        id="task-scheduler-type-filter"
                        value={typeFilter}
                        label="Type"
                        onChange={(e) => {
                            setTypeFilter(e.target.value);
                            writeStoredFilter(TYPE_FILTER_KEY, e.target.value);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                    >
                        {TYPE_FILTER_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                loading={loading}
                paginationMode="server"
                sortingMode="server"
                rowCount={totalRows}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                onRowClick={(params) => navigate(adminTaskSchedulerEditPath(params.id))}
                autoHeight
                getRowHeight={() => "auto"}
                sx={{
                    minHeight: 400,
                    "& .MuiDataGrid-row": { cursor: "pointer" },
                    "& .MuiDataGrid-cell": {
                        py: 1,
                        alignItems: "center",
                        whiteSpace: "normal",
                        overflow: "visible",
                        lineHeight: 1.4,
                    },
                    "& .MuiDataGrid-cellContent": {
                        whiteSpace: "normal",
                        overflow: "visible",
                        textOverflow: "clip",
                        wordBreak: "break-word",
                        lineHeight: 1.4,
                    },
                }}
            />

            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                title="Remove schedule?"
                message="This removes the row and reloads Quartz on the server. This cannot be undone."
                targetName={rowToDelete?.memo || rowToDelete?.cron || ""}
                targetId={rowToDelete?.id}
                callback={handleConfirmDelete}
                okText="Remove"
                cancelText="Cancel"
            />

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />
        </Box>
    );
}
